import CustomCard from '@/components/customCard';
import RegularText from '@/components/RegularText';
import { LocationConditionEnum } from '@/constants/enum';
import { openCrisp } from '@/helpers';
import { uniq } from '@/helpers/string';
import useUserPlan from '@/hooks/useUserPlan';
import ruleSlice, { ruleErrorsSelector, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import { Badge, InlineStack, Link, Scrollable, TextField } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { Tone } from '@shopify/polaris/build/ts/src/components/Badge';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConnectBadge from '../ConnectBadge';
import { ZipcodeConditionStyled } from './styled';
import checkoutRuleSlice, {
  checkoutRuleErrorsSelector,
  checkoutRuleSettingsSelector,
  currentOrderItemValueSelector,
} from '@/redux/slice/checkoutRule.slice';

interface IProps {
  onDismiss: () => void;
  badgeTone?: Tone;
  hasConnectBadge?: boolean;
  badgeContent?: string;
  isInCheckout?: boolean;
}

const ZipcodeCondition = (props: IProps) => {
  const dispatch = useDispatch();

  const currentOrderItemValue = useSelector(currentOrderItemValueSelector);
  const dataNormalRule = useSelector(ruleSettingsSelector);
  const dataCheckoutRule = useSelector(checkoutRuleSettingsSelector);
  const dataRuleLocations = useMemo(() => {
    return props.isInCheckout ? dataCheckoutRule.checkoutLocationConditions : dataNormalRule.locationConditions;
  }, [dataCheckoutRule, dataNormalRule, props.isInCheckout]);

  const errorsNormal = useSelector(ruleErrorsSelector);
  const errorsCheckout = useSelector(checkoutRuleErrorsSelector);

  const errors = useMemo(() => {
    return props.isInCheckout ? errorsCheckout : errorsNormal;
  }, [errorsCheckout, errorsNormal, props.isInCheckout]);

  const { isPlanLimited } = useUserPlan();

  const [state, setState] = useState('');

  const currentSlice = useMemo(() => {
    return props.isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [props.isInCheckout]);

  const postCodeList = useMemo(() => {
    return uniq(state.split(',').map((item) => item.trim()))?.filter((item) => item !== '');
  }, [state]);

  const handleSetError = useCallback(
    (value: string) => {
      dispatch(
        currentSlice.actions.handleSetError({
          value: { field: 'zipcode', msg: value },
        }),
      );
    },
    [currentSlice.actions, dispatch],
  );

  const handleUpdateRule = useCallback(
    (value: string) => {
      const indexOfOldZipcode = dataRuleLocations.findIndex((item) => item.type === LocationConditionEnum.Zipcode);
      const newDataLocationConditions = [...dataRuleLocations];
      if (indexOfOldZipcode !== -1) {
        const current = newDataLocationConditions[indexOfOldZipcode];
        newDataLocationConditions.splice(indexOfOldZipcode, 1, {
          type: LocationConditionEnum.Zipcode,
          value: [
            {
              data: value,
            },
          ],
          order: current?.order ?? currentOrderItemValue,
        });
        if (!current?.order || Number.isNaN(+current?.order)) {
          //@ts-expect-error
          if (props.isInCheckout) dispatch(currentSlice.actions.handleAddCurrentCount());
        }
      }

      dispatch(
        currentSlice.actions.handleChange({
          //@ts-ignore-next-line
          key: props.isInCheckout ? 'checkoutLocationConditions' : 'locationConditions',
          value: newDataLocationConditions,
        }),
      );

      handleSetError(value.length ? '' : 'Please enter at least 1 zipcode');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSlice.actions, JSON.stringify(dataRuleLocations), dispatch, handleSetError, props.isInCheckout],
  );

  const onRemovePostCode = (postCode: string) => {
    const listCode = [...state.split(',')];
    const newListCode = listCode?.filter((code) => {
      return code.trim() !== postCode;
    });
    setState(newListCode.join(','));
    const temp = uniq(newListCode.map((item) => item.trim()))?.filter((item) => item !== '');
    handleUpdateRule(temp?.toString());
  };

  const getZipcodeCondition = useCallback(() => {
    if (dataRuleLocations) {
      const stateCondition = dataRuleLocations.find((item) => item.type === LocationConditionEnum.Zipcode)?.value[0].data;
      return stateCondition;
    }
    return null;
  }, [dataRuleLocations]);

  useEffect(() => {
    const dataZipcodes = getZipcodeCondition();
    if (dataZipcodes) {
      setState(dataZipcodes);
    }
  }, [getZipcodeCondition]);

  return (
    <ZipcodeConditionStyled>
      {!!props.hasConnectBadge && <ConnectBadge tone={props.badgeTone}>{props.badgeContent}</ConnectBadge>}
      <CustomCard
        title={
          <div>
            <span className="mr-8">Zipcodes</span>
            <Badge>{postCodeList.length + ''}</Badge>
          </div>
        }
        textClassName="bold-text-styled"
        secondaryActions={{
          onAction: props.onDismiss,
          buttonProps: {
            icon: DeleteIcon,
            tone: 'critical',
            variant: 'plain',
          },
        }}
        collapsible
        defaultExpanded
      >
        <div className="mt-8">
          <RegularText>
            Enter numeric zipcodes separated by comma (,) or add range by colon (:).
            {!props.isInCheckout && (
              <>
                {' '}
                Have a lot to import?{' '}
                <Link onClick={openCrisp} monochrome>
                  Contact support
                </Link>
                .
              </>
            )}
          </RegularText>
        </div>
        <div className="mt-8">
          <TextField
            error={errors?.find((item) => item.field === 'zipcode')?.msg || ''}
            multiline={2}
            maxHeight={'500px'}
            label=""
            labelHidden
            value={state}
            onChange={(value) => {
              setState(value);
              handleSetError('');
            }}
            autoComplete="off"
            onBlur={() => {
              handleUpdateRule(postCodeList.toString());
            }}
            disabled={isPlanLimited}
          />
          <Scrollable className="post-tags" focusable>
            <InlineStack gap={'100'} wrap={true}>
              {uniq(state.split(',').map((item) => item.trim()))
                ?.filter((item) => item !== '')
                .splice(0, 500)
                .map((postCode) => {
                  return (
                    <div className="mt-8 mr-8 Polaris-Tag" key={postCode}>
                      <h1 title={postCode} className="Polaris-Tag__TagText">
                        {postCode}
                      </h1>
                      <button className="Polaris-Tag__Button" onClick={() => onRemovePostCode(postCode)}>
                        <div>X</div>
                      </button>
                    </div>
                  );
                })}
            </InlineStack>
          </Scrollable>
        </div>
      </CustomCard>
    </ZipcodeConditionStyled>
  );
};

export default ZipcodeCondition;
